import React, { Component } from "react";
import { Sun, Moon, Monitor } from "lucide-react";
import { useThemeStore, ThemeMode } from "../stores/themeStore";

interface ThemeToggleState {
    mode: ThemeMode;
}

/**
 * Pill-shaped theme toggle with Light / Dark / System modes.
 * Matches Wavebooks UI pattern.
 */
class ThemeToggle extends Component<Record<string, never>, ThemeToggleState> {
    private unsubscribe: (() => void) | null = null;

    constructor(props: Record<string, never>) {
        super(props);
        this.state = {
            mode: useThemeStore.getState().mode,
        };
    }

    componentDidMount() {
        this.unsubscribe = useThemeStore.subscribe((state) => {
            this.setState({ mode: state.mode });
        });
    }

    componentWillUnmount() {
        this.unsubscribe?.();
    }

    handleSetMode = (mode: ThemeMode) => {
        useThemeStore.getState().setMode(mode);
    };

    render() {
        const { mode } = this.state;

        const modes: { key: ThemeMode; icon: typeof Sun; label: string }[] = [
            { key: "light", icon: Sun, label: "Light" },
            { key: "dark", icon: Moon, label: "Dark" },
            { key: "system", icon: Monitor, label: "System" },
        ];

        return (
            <div className="flex items-center gap-1 p-1 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-neutral-800 dark:to-neutral-800/80 rounded-xl shadow-sm border border-gray-200/50 dark:border-neutral-700/50">
                {modes.map(({ key, icon: Icon, label }) => {
                    const isActive = mode === key;
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => this.handleSetMode(key)}
                            className={`
                                relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-300
                                ${isActive
                                    ? "bg-white dark:bg-neutral-700 text-primary-600 dark:text-primary-400 shadow-md scale-105"
                                    : "text-gray-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-white/50 dark:hover:bg-neutral-700/50 hover:scale-105"
                                }
                            `}
                            aria-label={`Switch to ${label} mode`}
                            title={`${label} mode`}
                        >
                            {isActive && (
                                <div className="absolute inset-0 bg-primary-400/20 dark:bg-primary-500/20 rounded-lg blur-sm -z-10" />
                            )}
                            <Icon className="h-4 w-4" />
                            <span className="hidden sm:inline">{label}</span>
                        </button>
                    );
                })}
            </div>
        );
    }
}

export default ThemeToggle;
